import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useScanning } from '../../../../scanning/scanning.service'
import { OrderDetailContext } from '../OrderDetailContext'
import { UltyModalWrapperContext } from '../../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import OrderPickupWrongItem from './OrderPickupWrongItem'
import OrderPickupTooMuchItem from './OrderPickupTooMuchItem'
import OrderPickupMoreItems from './OrderPickupMoreItems'
import PropTypes from 'prop-types'
import OrderPickupItemComplete from './OrderPickupItemComplete'
import { useSaveFulfillment } from '../OrderItemList/OrderItemFulfillment/useSaveFulfillment'
import { useNavigate } from 'react-router-dom-v5-compat'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { MOBILE_THRESHOLD } from '../../../../constants'
import { AdjustWeight } from '../OrderItemList/OrderItemFulfillment/AdjustWeight'
import { convertOrderItemsToFulfillmentIssues } from '../../../../services/Order/Order.service'


const OrderPickingScanContext = createContext({
    scannedItems: [],
    pickingMode: 'MANUAL',
    scannedItemsMap: new Map(),
})


export const OrderPickingScanProvider = ({
                                             order,
                                             onFulfillmentIssue,
                                             clearFulfillmentIssue,
                                             children,
                                         }) => {
    const [pickingMode, setPickingMode] = useState('MANUAL')

    const retrieveScannedFromOrderItems = (orderItems) => {
        return orderItems?.filter(oi => oi.pickingMethod === 'SCAN')?.map(oi => [oi.id, oi.quantity]) ?? []
    }

    const [scannedItemsMap, setScannedItemsMap] = useState(new Map(retrieveScannedFromOrderItems(order?.orderItems)))
    const [latestScannedItem, setLatestScannedItem] = useState(null)
    const {isInReplacement} = useContext(OrderDetailContext)
    const navigate = useNavigate()
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)

    //Reset scanned when orderItems is updated : Scanned fields should have been updated
    useEffect(() => {
        setScannedItemsMap(new Map(retrieveScannedFromOrderItems(order?.orderItems)))
    }, [order.orderItems])


    const addScannedItem = useCallback((itemId, howManyTime = undefined) => {
        setScannedItemsMap(prev => {
            const nextMap = new Map(prev)
            const current = nextMap.get(itemId) || 0
            const nextVal = current + (howManyTime ?? 1)
            nextMap.set(itemId, nextVal)
            return nextMap
        })
        setLatestScannedItem(itemId)
    }, [])

    const scannedItems = useMemo(() => {
        return Array.from(scannedItemsMap.entries()).reduce((acc, [itemId, quantity]) => {
            return [...acc, ...Array(quantity).fill(itemId)]
        }, [])
    }, [scannedItemsMap])

    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)

    const showAdjustWeightModal = (orderItem) => {
        const modalSettings = {
            size: 'tiny',
            component: <AdjustWeight
              orderId={order.id}
              platformModule={order.platform.module}
              orderItem={orderItem}
              pickingMethod='SCAN'
              onSubmit={() => {
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }
    const alertWrongItem = (barcode) => {
        const replacableOrderItems = order?.orderItems.filter(item =>
          //Should be defined as replaceable
          item.isReplaceable
          // Musn't be already related to a fulfillment issue
          // This should evolves in the future, but current implementation can't handle fulfillment issue modification
          && !item.fulfillmentIssue
          // Wasn't already scanned
          && (!scannedItemsMap?.has(item.id)),
        )
        const modalSettings = {
            size: 'tiny',
            component: <OrderPickupWrongItem replacableOrderItems={replacableOrderItems} barcode={barcode}
                                             onSubmit={(action, {orderItem} = {}) => {
                                                 if (action === 'SUBSTITUTE') {
                                                     navigate(`items/${orderItem.id}/replacement?preloadedSearch=${barcode}&initialPickingMethod=SCAN&hideNavBar=${isMobile}`)
                                                 }
                                             }}/>,
        }

        handleUltyModalWrapper(true, modalSettings)
    }
    const alertTooMuchItem = (orderItem, barcode) => {
        const modalSettings = {
            size: 'tiny',
            component: <OrderPickupTooMuchItem barcode={barcode} orderItem={orderItem} onSubmit={() => {
            }}/>,
        }

        handleUltyModalWrapper(true, modalSettings)
    }
    const alertRemainingItem = (orderItem, currentQuantity, expectedQuantity) => {
        const modalSettings = {
            size: 'tiny',
            component: <OrderPickupMoreItems orderItem={orderItem} currentQuantity={currentQuantity}
                                             expectedQuantity={expectedQuantity} onSubmit={({type}) => {

                if (type === 'CANCEL_ORDER') {
                    onFulfillmentIssue(orderItem, 0)
                }

                if (type === 'UNAVAILABLE') {
                    onFulfillmentIssue(orderItem, currentQuantity)
                }

                if (type === 'VALIDATE') {
                    addScannedItem(orderItem.id, expectedQuantity - currentQuantity)
                    foundItemMutation.mutate({orderItem, pickingMethod: 'SCAN'})
                    clearFulfillmentIssue(orderItem)
                }
            }}/>,
        }

        handleUltyModalWrapper(true, modalSettings)
    }
    const alertItemComplete = (orderItem) => {
        const modalSettings = {
            size: 'tiny',
            component: <OrderPickupItemComplete orderItem={orderItem}/>,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const {saveFulfillmentIssuesMutation, foundItemMutation} = useSaveFulfillment(order.id)

    const onScan = useCallback((event) => {
        //Ignore events when already doing a replacement. Scanning is handle directly by the replacement component
        if (isInReplacement) {
            return
        }

        if (!order.isModifiable) {
            return
        }

        setPickingMode('SCANNING')

        const scannedBarcode = event.value

        const orderItem = order.orderItems.find(i => parseInt(i.object?.barcode || '0') === parseInt(scannedBarcode) || i.object?.barcode === scannedBarcode)
        if (orderItem) {
            if (orderItem.item.pricedByUnit?.type !== 'COUNT') {
                addScannedItem(orderItem.id, orderItem.quantity)
                showAdjustWeightModal(orderItem)
                return
            }

            addScannedItem(orderItem.id)
            const alreadyScanned = scannedItemsMap.get(orderItem.id) || 0
            const current = alreadyScanned + 1

            if (orderItem.quantity > current) {
                alertRemainingItem(orderItem, current, orderItem.quantity)
            } else if (orderItem.quantity < current) {
                alertTooMuchItem(orderItem, scannedBarcode)
            } else {
                alertItemComplete(orderItem)

                if (current === orderItem.initialQuantity) {
                    foundItemMutation.mutate({orderItem, pickingMethod: 'SCAN'})
                } else {
                    saveFulfillmentIssuesMutation.mutate({
                        fulfillmentIssues: convertOrderItemsToFulfillmentIssues(
                          [{...orderItem, quantity: current}],
                          [],
                        ),
                    })
                }
            }
        } else {
            alertWrongItem(scannedBarcode)
        }

    }, [isInReplacement, order.isModifiable, order.orderItems, addScannedItem, scannedItemsMap, showAdjustWeightModal, alertRemainingItem, alertTooMuchItem, alertItemComplete, foundItemMutation, saveFulfillmentIssuesMutation, alertWrongItem])

    useScanning(onScan)

    const contextValue = useMemo(() => ({
        pickingMode,
        scannedItems,
        scannedItemsMap,
        addScannedItem,
        latestScannedItem,
    }), [pickingMode, scannedItems, scannedItemsMap, addScannedItem, latestScannedItem])

    return (
      <OrderPickingScanContext.Provider value={contextValue}>
          {children}
      </OrderPickingScanContext.Provider>
    )

}

OrderPickingScanProvider.propTypes = {
    order: PropTypes.object.isRequired,
    onFulfillmentIssue: PropTypes.func.isRequired,
    clearFulfillmentIssue: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
}

export const useOrderPickingScan = () => {
    return useContext(OrderPickingScanContext)
}
